import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Search,
  User,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Check,
  ShoppingCart,
  AlertTriangle,
  Baby,
  Droplets,
  Shield,
  Heart,
  Printer,
  MessageCircle,
  Plus,
  Minus,
  X,
  Info,
  Users,
  History,
  UserPlus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Baby age categories
const BABY_AGE_CATEGORIES = [
  { id: '0-3', label: '0 - 3 mois', description: 'Nouveau-né' },
  { id: '3-12', label: '3 - 12 mois', description: 'Nourrisson' },
  { id: '12-36', label: '12 - 36 mois', description: 'Jeune enfant' }
];

// Baby skin concerns
const BABY_CONCERNS = [
  { id: 'dry_skin', label: 'Peau sèche', icon: '🏜️', description: 'Peau rugueuse, qui tire' },
  { id: 'sensitive_skin', label: 'Peau sensible / réactive', icon: '⚡', description: 'Réactions, irritabilité' },
  { id: 'diaper_rash', label: 'Érythème fessier', icon: '🍑', description: 'Rougeurs au niveau des fesses' },
  { id: 'cradle_cap', label: 'Croûtes de lait', icon: '👶', description: 'Squames sur le cuir chevelu' },
  { id: 'irritation', label: 'Irritation', icon: '😣', description: 'Zone irritée, inconfort' },
  { id: 'heat_rash', label: 'Bourbouilles', icon: '🌡️', description: 'Boutons de chaleur' },
  { id: 'redness', label: 'Rougeurs', icon: '🔴', description: 'Zones rouges localisées' },
  { id: 'daily_hygiene', label: 'Hygiène quotidienne', icon: '🛁', description: 'Soins de base du bébé' }
];

// Routine steps for babies
const ROUTINE_STEPS = {
  cleanse: { label: 'Nettoyer', icon: '🧴', color: 'bg-blue-100 text-blue-700' },
  moisturize: { label: 'Hydrater', icon: '💧', color: 'bg-cyan-100 text-cyan-700' },
  protect: { label: 'Protéger', icon: '🛡️', color: 'bg-green-100 text-green-700' },
  treat: { label: 'Traiter', icon: '💊', color: 'bg-amber-100 text-amber-700' }
};

const BabyDiagnosis = ({ language = 'fr', onComplete, onCancel, onViewProducts }) => {
  // Steps: 1 = Client Info, 2 = Baby Age, 3 = Concerns, 4 = Results
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Client/Baby info
  const [parentPhone, setParentPhone] = useState('');
  const [identMode, setIdentMode] = useState('phone'); // 'phone' | 'code'
  const [familyCode, setFamilyCode] = useState('');
  const [parentBirthYear, setParentBirthYear] = useState('');
  const [generatedFamilyCode, setGeneratedFamilyCode] = useState(null);
  const [codeLookupError, setCodeLookupError] = useState(null);
  const [babyName, setBabyName] = useState('');
  const [babyAge, setBabyAge] = useState('');
  const [babyWeight, setBabyWeight] = useState('');
  const [babySex, setBabySex] = useState('');
  const [babyAllergies, setBabyAllergies] = useState('');
  const [existingClient, setExistingClient] = useState(null);
  
  // Family management
  const [familyData, setFamilyData] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [showCreateChild, setShowCreateChild] = useState(false);
  const [parentFirstName, setParentFirstName] = useState('');
  const [parentLastName, setParentLastName] = useState('');
  
  // Baby age category
  const [ageCategory, setAgeCategory] = useState('');
  
  // Concerns
  const [selectedConcerns, setSelectedConcerns] = useState([]);
  
  // Results
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  
  // Cart state (unified with adult)
  const [cart, setCart] = useState([]);
  
  // Manual product search
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [searchingFamily, setSearchingFamily] = useState(false);
  const [manualProducts, setManualProducts] = useState([]);

  // ============ CART FUNCTIONS (Same as adult) ============
  const addToCart = (product) => {
    const productId = product.id || product.code || product.name;
    setCart(prev => {
      const existing = prev.find(item => item.product_id === productId);
      if (existing) {
        return prev.map(item => 
          item.product_id === productId 
            ? { ...item, quantity: item.quantity + 1 } 
            : item
        );
      }
      return [...prev, {
        product_id: productId,
        name: product.name,
        price: product.price || 0,
        quantity: 1,
        routine_step: product.routine_step,
        for_concern: product.for_concern
      }];
    });
    toast.success(`${product.name} ajouté au panier`);
  };

  const updateCartQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => 
      item.product_id === productId 
        ? { ...item, quantity: Math.max(1, item.quantity + delta) } 
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
    toast.info('Produit retiré du panier');
  };

  const isInCart = (productId) => cart.some(item => item.product_id === productId);
  const getCartQuantity = (productId) => cart.find(item => item.product_id === productId)?.quantity || 0;
  
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Add all recommended products to cart
  const addAllToCart = () => {
    if (diagnosisResult?.products) {
      diagnosisResult.products.forEach(product => {
        const productId = product.id || product.code || product.name;
        if (!isInCart(productId)) {
          setCart(prev => [...prev, {
            product_id: productId,
            name: product.name,
            price: product.price || 0,
            quantity: 1,
            routine_step: product.routine_step,
            for_concern: product.for_concern
          }]);
        }
      });
      toast.success(`${diagnosisResult.products.length} produits ajoutés au panier`);
    }
  };

  // Search family on phone input
  useEffect(() => {
    if (identMode !== 'phone') return;
    const searchFamily = async () => {
      if (parentPhone.length >= 10) {
        setSearchingFamily(true);
        try {
          const token = localStorage.getItem('dermascan_token');
          // Search for family
          const familyResponse = await axios.get(`${API_URL}/family/search/${parentPhone}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (familyResponse.data.found) {
            setFamilyData(familyResponse.data.family);
            setExistingClient({ client: familyResponse.data.family });
          } else if (familyResponse.data.existing_client) {
            setExistingClient({ client: familyResponse.data.existing_client });
            setFamilyData(null);
          } else {
            setFamilyData(null);
            setExistingClient(null);
          }
        } catch (error) {
          console.error('Error searching family:', error);
          // Fallback to client search
          try {
            const token = localStorage.getItem('dermascan_token');
            const response = await axios.get(`${API_URL}/comprehensive/client/${parentPhone}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.client) {
              setExistingClient(response.data);
            }
          } catch (err) {
            console.error('Client search also failed:', err);
          }
        } finally {
          setSearchingFamily(false);
        }
      } else {
        setFamilyData(null);
        setExistingClient(null);
        setSearchingFamily(false);
      }
    };
    
    const timer = setTimeout(searchFamily, 500);
    return () => clearTimeout(timer);
  }, [parentPhone, identMode]);

  // Select existing child from family
  const selectChild = (child) => {
    setSelectedChild(child);
    setBabyName(child.first_name);
    setBabyAge(child.age_months?.toString() || '');
    setBabySex(child.sex || '');
    setShowCreateChild(false);
    toast.success(`${child.first_name} sélectionné(e)`);
  };

  // Create new child in family
  const createNewChild = async () => {
    if (!babyName.trim()) {
      toast.error('Veuillez entrer le prénom du bébé');
      return;
    }
    
    try {
      const token = localStorage.getItem('dermascan_token');
      
      if (familyData) {
        // Add child to existing family
        const response = await axios.post(
          `${API_URL}/family/${familyData.id}/children`,
          {
            first_name: babyName,
            age_months: babyAge ? parseInt(babyAge) : null,
            sex: babySex || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.duplicate_found) {
          // Offer to select existing child
          toast.info(`${babyName} existe déjà. Sélection automatique.`);
          selectChild(response.data.existing_child);
          return;
        }
        
        setSelectedChild(response.data.child);
        toast.success(`${babyName} ajouté(e) à la famille`);
      } else {
        // Create new family
        const response = await axios.post(
          `${API_URL}/family/create`,
          {
            parent_phone: identMode === 'phone' ? parentPhone : null,
            parent_first_name: parentFirstName || `Parent de ${babyName}`,
            parent_last_name: parentLastName || '',
            parent_birth_year: parentBirthYear ? parseInt(parentBirthYear) : null,
            children: [{
              first_name: babyName,
              age_months: babyAge ? parseInt(babyAge) : null,
              sex: babySex || null
            }]
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Si un code famille a été généré (création sans téléphone) → on le mémorise pour affichage
        const newCode = response.data?.generated_family_code || response.data?.family_code;
        if (newCode && identMode === 'code' && !familyCode) {
          setFamilyCode(newCode);
          setGeneratedFamilyCode(newCode);
        }

        setSelectedChild({ id: response.data.id, first_name: babyName });
        toast.success(newCode && identMode === 'code'
          ? `Fiche famille créée — code : ${newCode}`
          : 'Fiche famille créée');
      }
      
      setShowCreateChild(false);
    } catch (error) {
      console.error('Error creating child:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Toggle concern selection
  const toggleConcern = (concernId) => {
    setSelectedConcerns(prev => 
      prev.includes(concernId) 
        ? prev.filter(c => c !== concernId)
        : [...prev, concernId]
    );
  };

  // Search products for manual addition
  const searchProductsManual = async (query) => {
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      return;
    }
    
    setSearchingProducts(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/baby/products/search?q=${encodeURIComponent(query)}&limit=15`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProductSearchResults([]);
    } finally {
      setSearchingProducts(false);
    }
  };

  // Debounced product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchQuery.length >= 2) {
        searchProductsManual(productSearchQuery);
      } else {
        setProductSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchQuery]);

  // Submit diagnosis
  const submitDiagnosis = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('dermascan_token');
      
      const payload = {
        parent_phone: parentPhone,
        baby_name: babyName,
        baby_age_months: babyAge ? parseInt(babyAge) : null,
        baby_weight_kg: babyWeight ? parseFloat(babyWeight) : null,
        baby_sex: babySex,
        baby_allergies: babyAllergies || null,
        age_category: ageCategory,
        concerns: selectedConcerns,
        child_id: selectedChild?.id || null,
        family_id: familyData?.id || null
      };
      
      const response = await axios.post(`${API_URL}/baby/diagnosis`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDiagnosisResult(response.data);

      // Products are shown as SUGGESTIONS only. The agent manually validates
      // each product by clicking "Ajouter" on the ones they want in the cart.
      // This allows the agent to substitute or skip products based on what's
      // actually available or preferred. Quantity controls remain unchanged.

      setStep(4); // Go to results
      
    } catch (error) {
      console.error('Error creating diagnosis:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du diagnostic');
    } finally {
      setLoading(false);
    }
  };

  // Validation helpers
  const canProceedStep1 = (
    (identMode === 'phone' ? parentPhone.length >= 10 : true) &&
    babyName.trim()
  );
  const canProceedStep2 = ageCategory !== '';
  const canProceedStep3 = selectedConcerns.length > 0;

  // Get concern label
  const getConcernLabel = (concernId) => {
    return BABY_CONCERNS.find(c => c.id === concernId)?.label || concernId;
  };

  // Step 1: Client Info
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-#E8B4B8 to-#B07F83 rounded-full flex items-center justify-center mb-3">
          <Baby className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-800">Conseil Bébé</h2>
        <p className="text-sm text-gray-500">Étape 1/3 - Informations</p>
      </div>
      
      {/* Toggle d'identification : Téléphone (recommandé) / Code famille */}
      <div className="bg-zinc-50 border-2 border-zinc-200 rounded-lg p-3">
        <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2">
          Mode d'identification du parent
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setIdentMode('phone'); setCodeLookupError(null); }}
            data-testid="baby-ident-mode-phone"
            className={`flex-1 min-h-[48px] px-3 py-2 rounded-lg font-semibold text-sm border-2 transition active:scale-95 ${
              identMode === 'phone'
                ? 'bg-pink-600 text-white border-pink-600 shadow'
                : 'bg-white text-zinc-700 border-zinc-200'
            }`}
          >
            📱 Téléphone <span className="text-xs opacity-80">(recommandé)</span>
          </button>
          <button
            type="button"
            onClick={() => { setIdentMode('code'); setParentPhone(''); }}
            data-testid="baby-ident-mode-code"
            className={`flex-1 min-h-[48px] px-3 py-2 rounded-lg font-semibold text-sm border-2 transition active:scale-95 ${
              identMode === 'code'
                ? 'bg-[#5B3B8C] text-white border-[#5B3B8C] shadow'
                : 'bg-white text-zinc-700 border-zinc-200'
            }`}
          >
            🔑 Code famille
          </button>
        </div>
        <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
          {identMode === 'phone'
            ? 'Le numéro permet le suivi WhatsApp et le rappel des conseils.'
            : 'Pour les parents qui ne souhaitent pas donner leur téléphone. Un code court sera généré (ex: FA-K7M3).'}
        </p>
      </div>

      {/* Parent Phone */}
      {identMode === 'phone' && (
      <div>
        <Label>Téléphone du parent *</Label>
        <div className="relative">
          <Input
            type="tel"
            inputMode="tel"
            placeholder="07 XX XX XX XX"
            value={parentPhone}
            onChange={(e) => setParentPhone(e.target.value)}
            className="text-lg h-12"
            data-testid="baby-parent-phone"
          />
          {searchingFamily && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="w-4 h-4 animate-spin text-pink-500" />
            </div>
          )}
        </div>
      </div>
      )}

      {/* Code famille (mode code) */}
      {identMode === 'code' && (
      <div className="space-y-3">
        <div>
          <Label>Code famille (laisser vide pour un nouveau parent)</Label>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="FA-XXXX"
              value={familyCode}
              onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
              className="text-lg h-12 font-mono uppercase tracking-wider flex-1"
              maxLength={7}
              data-testid="baby-family-code-input"
            />
            <Button
              type="button"
              onClick={async () => {
                if (!familyCode || familyCode.length < 5) {
                  setCodeLookupError('Code trop court (FA-XXXX)');
                  return;
                }
                setCodeLookupError(null);
                try {
                  const token = localStorage.getItem('dermascan_token');
                  const params = new URLSearchParams();
                  if (parentFirstName) params.set('first_name', parentFirstName);
                  if (parentBirthYear) params.set('parent_birth_year', parentBirthYear);
                  const url = `${API_URL}/family/by-code/${encodeURIComponent(familyCode.toUpperCase())}${params.toString() ? '?' + params.toString() : ''}`;
                  const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
                  if (r.data?.family) {
                    setFamilyData(r.data.family);
                    setParentFirstName(r.data.family.parent_first_name || '');
                    setParentLastName(r.data.family.parent_last_name || '');
                  }
                } catch (e) {
                  setCodeLookupError(e?.response?.data?.detail || 'Code introuvable');
                  setFamilyData(null);
                }
              }}
              disabled={!familyCode || familyCode.length < 5}
              className="h-12 bg-[#5B3B8C] text-white hover:bg-[#4A2F75] font-semibold px-4"
              data-testid="baby-family-code-search-btn"
            >
              🔍 Chercher
            </Button>
          </div>
          {codeLookupError && (
            <p className="text-xs text-red-600 mt-1">{codeLookupError}</p>
          )}
          <p className="text-[11px] text-zinc-500 mt-1">
            Si la famille n'a jamais été enregistrée, laissez vide → un code sera généré à la création.
          </p>
        </div>
        <div>
          <Label>Année de naissance du parent <span className="text-zinc-400 font-normal">(facultatif, sécurise la recherche)</span></Label>
          <Input
            type="number"
            inputMode="numeric"
            placeholder="ex: 1990"
            value={parentBirthYear}
            onChange={(e) => setParentBirthYear(e.target.value)}
            className="text-base h-12"
            min="1900"
            max="2030"
            data-testid="baby-parent-birth-year-input"
          />
        </div>
      </div>
      )}

      {/* Bandeau code famille généré (après création) */}
      {generatedFamilyCode && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4" data-testid="baby-generated-code-banner">
          <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-1">
            ⚠️ Code famille à communiquer au parent
          </p>
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono font-bold text-2xl sm:text-3xl text-amber-900 tracking-wider select-all">
              {generatedFamilyCode}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-10 border-amber-300 text-amber-700"
              onClick={() => { try { navigator.clipboard.writeText(generatedFamilyCode); } catch {/* ignore */} }}
            >
              📋 Copier
            </Button>
          </div>
          <Button
            type="button"
            className="mt-3 w-full h-12 bg-[#E91E63] hover:bg-[#ad1457] text-white font-semibold"
            data-testid="print-family-card-btn"
            onClick={async () => {
              try {
                const token = localStorage.getItem('dermascan_token');
                const r = await axios.get(`${API_URL}/family/card/${generatedFamilyCode}`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: 'blob',
                });
                const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                const w = window.open(blobUrl, '_blank');
                if (!w) {
                  const a = document.createElement('a');
                  a.href = blobUrl;
                  a.download = `carte_famille_${generatedFamilyCode}.pdf`;
                  a.click();
                }
              } catch (e) {
                toast.error('Erreur génération carte');
              }
            }}
          >
            🖨 Imprimer la carte famille (85×54 mm)
          </Button>
          <p className="text-xs text-amber-700 mt-2 leading-relaxed">
            ✍️ Notez ce code sur un papier OU imprimez la carte chic ci-dessus à donner au parent.
          </p>
        </div>
      )}
      
      {/* Family Card - Show if family exists */}
      {familyData && (
        <Card className="border-green-300 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-700 mb-3">
              <Users className="w-5 h-5" />
              <span className="font-semibold">Fiche famille trouvée</span>
            </div>
            <p className="text-sm text-green-600 mb-3">
              {familyData.parent_first_name} {familyData.parent_last_name}
            </p>
            
            {/* Children list */}
            {familyData.children?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-green-700">Enfants enregistrés :</p>
                {familyData.children.map(child => (
                  <div 
                    key={child.id}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                      selectedChild?.id === child.id 
                        ? 'bg-green-200 ring-2 ring-green-500' 
                        : 'bg-white hover:bg-green-100'
                    }`}
                    onClick={() => selectChild(child)}
                    data-testid={`select-child-${child.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Baby className="w-4 h-4 text-green-600" />
                      <span className="font-medium">{child.first_name}</span>
                      {child.age_months && (
                        <Badge variant="outline" className="text-xs">
                          {child.age_months} mois
                        </Badge>
                      )}
                    </div>
                    {child.diagnosis_count > 0 && (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <History className="w-3 h-3 mr-1" />
                        {child.diagnosis_count} conseil{child.diagnosis_count > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {selectedChild?.id === child.id && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Add new child button */}
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-3 border-green-400 text-green-700 hover:bg-green-100"
              onClick={() => {
                setSelectedChild(null);
                setBabyName('');
                setBabyAge('');
                setBabySex('');
                setShowCreateChild(true);
              }}
              data-testid="add-new-child-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Ajouter un nouvel enfant
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Existing client indicator (no family) */}
      {!familyData && existingClient?.client && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-blue-700">
            <User className="w-4 h-4" />
            <span className="font-medium">Client existant</span>
          </div>
          <p className="text-sm text-blue-600 mt-1">
            {existingClient.client.first_name} {existingClient.client.last_name}
          </p>
          <p className="text-xs text-blue-500 mt-2">
            Une fiche famille sera créée lors du premier conseil bébé.
          </p>
        </div>
      )}
      
      {/* New family creation prompt */}
      {parentPhone.length >= 10 && !familyData && !existingClient?.client && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-amber-700">
            <UserPlus className="w-4 h-4" />
            <span className="font-medium">Nouveau client</span>
          </div>
          <p className="text-xs text-amber-600 mt-1">
            Une fiche famille sera créée automatiquement.
          </p>
          {/* Optional: Parent name fields */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Input
              placeholder="Prénom parent"
              value={parentFirstName}
              onChange={(e) => setParentFirstName(e.target.value)}
              className="text-sm"
            />
            <Input
              placeholder="Nom parent"
              value={parentLastName}
              onChange={(e) => setParentLastName(e.target.value)}
              className="text-sm"
            />
          </div>
        </div>
      )}
      
      {/* Baby Name - Only show if no child selected or creating new */}
      {(!selectedChild || showCreateChild) && (
        <div>
          <Label>Prénom du bébé *</Label>
          <Input
            placeholder="Prénom"
            value={babyName}
            onChange={(e) => setBabyName(e.target.value)}
            data-testid="baby-name-input"
          />
        </div>
      )}
      
      {/* Baby Age, Weight & Sex */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Âge (mois)</Label>
          <Input
            type="number"
            placeholder="Ex: 6"
            value={babyAge}
            onChange={(e) => setBabyAge(e.target.value)}
            min="0"
            max="36"
            data-testid="baby-age-input"
            disabled={selectedChild && !showCreateChild}
          />
        </div>
        <div>
          <Label>Poids (kg)</Label>
          <Input
            type="number"
            placeholder="Ex: 7.5"
            value={babyWeight}
            onChange={(e) => setBabyWeight(e.target.value)}
            min="0"
            max="20"
            step="0.1"
            data-testid="baby-weight-input"
          />
        </div>
        <div>
          <Label>Sexe</Label>
          <Select value={babySex} onValueChange={setBabySex} disabled={selectedChild && !showCreateChild}>
            <SelectTrigger data-testid="baby-sex-select">
              <SelectValue placeholder="Choisir" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="female">Fille</SelectItem>
              <SelectItem value="male">Garçon</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Allergies */}
      <div>
        <Label>Allergies connues</Label>
        <Input
          placeholder="Ex: Parfum, lanoline, arachide..."
          value={babyAllergies}
          onChange={(e) => setBabyAllergies(e.target.value)}
          data-testid="baby-allergies-input"
        />
        <p className="text-xs text-gray-500 mt-1">Laisser vide si aucune allergie connue</p>
      </div>
      
      {/* Info Box */}
      <div className="bg-[#FDF2F3] border border-[#F5D0D3] rounded-lg p-4 flex gap-3">
        <Info className="w-5 h-5 text-[#C08B8F] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-pink-700">
          <p className="font-medium mb-1">Parcours dédié aux bébés</p>
          <p className="text-[#B07F83]">
            Ce parcours propose des conseils adaptés aux nourrissons de 0 à 36 mois avec des formulations douces.
          </p>
        </div>
      </div>
      
      {/* Save New Child Button - Show when creating new child in existing family */}
      {showCreateChild && familyData && babyName.trim() && (
        <Button 
          onClick={createNewChild}
          className="w-full bg-green-600 hover:bg-green-700"
          data-testid="save-new-child-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Enregistrer {babyName} dans la famille
        </Button>
      )}
    </div>
  );

  // Step 2: Age Category
  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#E8B4B8] to-[#B07F83] rounded-full flex items-center justify-center mb-3">
          <Heart className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-800">Tranche d'âge</h2>
        <p className="text-sm text-gray-500">Étape 2/3 - Catégorie d'âge</p>
      </div>
      
      <p className="text-center text-gray-600 mb-4">
        Sélectionnez la tranche d'âge de <span className="font-semibold text-[#B07F83]">{babyName}</span>
      </p>
      
      <div className="space-y-3">
        {BABY_AGE_CATEGORIES.map(category => (
          <Card 
            key={category.id}
            className={`cursor-pointer transition-all ${
              ageCategory === category.id 
                ? 'ring-2 ring-[#C08B8F] bg-[#FDF2F3] border-[#E8B4B8]' 
                : 'hover:border-[#F5D0D3] hover:bg-[#FDF2F3]/50'
            }`}
            onClick={() => setAgeCategory(category.id)}
            data-testid={`age-category-${category.id}`}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800">{category.label}</h3>
                <p className="text-sm text-gray-500">{category.description}</p>
              </div>
              {ageCategory === category.id && (
                <div className="w-8 h-8 bg-[#C08B8F] rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Step 3: Concerns
  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-#E8B4B8 to-#B07F83 rounded-full flex items-center justify-center mb-3">
          <Droplets className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-800">Problématiques</h2>
        <p className="text-sm text-gray-500">Étape 3/3 - Besoins du bébé</p>
      </div>
      
      <p className="text-center text-gray-600 mb-4">
        Quels sont les besoins de <span className="font-semibold text-[#B07F83]">{babyName}</span> ?
      </p>
      
      <div className="grid grid-cols-2 gap-3">
        {BABY_CONCERNS.map(concern => (
          <Card 
            key={concern.id}
            className={`cursor-pointer transition-all ${
              selectedConcerns.includes(concern.id) 
                ? 'ring-2 ring-[#C08B8F] bg-[#FDF2F3] border-[#E8B4B8]' 
                : 'hover:border-[#F5D0D3] hover:bg-[#FDF2F3]/30'
            }`}
            onClick={() => toggleConcern(concern.id)}
            data-testid={`concern-${concern.id}`}
          >
            <CardContent className="p-3 text-center">
              <span className="text-2xl mb-2 block">{concern.icon}</span>
              <h4 className="font-medium text-sm text-gray-800">{concern.label}</h4>
              <p className="text-xs text-gray-500 mt-1">{concern.description}</p>
              {selectedConcerns.includes(concern.id) && (
                <div className="mt-2">
                  <Check className="w-4 h-4 text-[#C08B8F] mx-auto" />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      
      {selectedConcerns.length > 0 && (
        <div className="mt-4 p-3 bg-[#FDF2F3] rounded-lg">
          <p className="text-sm text-pink-700 font-medium">
            {selectedConcerns.length} problématique{selectedConcerns.length > 1 ? 's' : ''} sélectionnée{selectedConcerns.length > 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );

  // Step 4: Results
  const renderStep4 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-3">
          <Check className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-800">Recommandations</h2>
        <p className="text-sm text-gray-500">Pour {babyName}</p>
      </div>
      
      {/* Age Category Badge */}
      <div className="flex flex-wrap justify-center gap-2">
        <Badge className="bg-[#FAE5E7] text-pink-700 border-[#E8B4B8]">
          <Baby className="w-3 h-3 mr-1" />
          {BABY_AGE_CATEGORIES.find(c => c.id === ageCategory)?.label}
        </Badge>
        {babyWeight && (
          <Badge variant="outline" className="text-gray-600">
            ⚖️ {babyWeight} kg
          </Badge>
        )}
      </div>
      
      {/* Allergies Warning */}
      {babyAllergies && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">Allergies signalées</p>
            <p className="text-sm text-red-600">{babyAllergies}</p>
          </div>
        </div>
      )}
      
      {/* Concerns Summary */}
      <div className="flex flex-wrap justify-center gap-2 mb-4">
        {selectedConcerns.map(concernId => (
          <Badge key={concernId} variant="outline" className="text-xs">
            {getConcernLabel(concernId)}
          </Badge>
        ))}
      </div>
      
      {/* Routine Recommendations */}
      {diagnosisResult?.routine && (
        <Card className="border-[#F5D0D3]">
          <CardHeader className="bg-gradient-to-r from-#C08B8F to-#B07F83 text-white py-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Routine conseillée
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {Object.entries(diagnosisResult.routine).map(([stepKey, stepData]) => {
              const stepInfo = ROUTINE_STEPS[stepKey];
              if (!stepData || !stepInfo) return null;
              return (
                <div key={stepKey} className={`p-3 rounded-lg ${stepInfo.color}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{stepInfo.icon}</span>
                    <span className="font-semibold">{stepInfo.label}</span>
                  </div>
                  <p className="text-sm">{stepData.advice}</p>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
      
      {/* Product Recommendations with Cart Controls */}
      {diagnosisResult?.products?.length > 0 && (
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white py-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Produits recommandés</span>
              <Button
                size="sm"
                variant="secondary"
                className="bg-white/20 hover:bg-white/30 text-white text-xs"
                onClick={addAllToCart}
                data-testid="add-all-to-cart"
              >
                <ShoppingCart className="w-3 h-3 mr-1" />
                Tout ajouter
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {diagnosisResult.products.map((product, pIndex) => {
                const productId = product.id || product.code || product.name;
                const inCart = isInCart(productId);
                const qty = getCartQuantity(productId);
                
                return (
                  <div key={pIndex} className={`p-3 transition-colors ${inCart ? 'bg-cyan-50' : 'hover:bg-gray-50'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-gray-800">{product.name}</p>
                          {inCart && <Check className="w-4 h-4 text-cyan-600" />}
                        </div>
                        {product.routine_step && (
                          <Badge variant="outline" className="text-xs mt-1">
                            {ROUTINE_STEPS[product.routine_step]?.label || product.routine_step}
                          </Badge>
                        )}
                      </div>
                      <p className="font-semibold text-cyan-700 whitespace-nowrap">
                        {product.price?.toLocaleString('fr-FR')} F
                      </p>
                    </div>
                    
                    {/* Cart Controls */}
                    <div className="flex items-center justify-end mt-2 gap-2">
                      {inCart ? (
                        <>
                          <div className="flex items-center gap-1 bg-gray-100 rounded-full px-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 rounded-full hover:bg-gray-200"
                              onClick={() => updateCartQuantity(productId, -1)}
                              data-testid={`decrease-${productId}`}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-6 text-center text-sm font-medium">{qty}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 rounded-full hover:bg-gray-200"
                              onClick={() => updateCartQuantity(productId, 1)}
                              data-testid={`increase-${productId}`}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 rounded-full text-red-500 hover:bg-red-100"
                            onClick={() => removeFromCart(productId)}
                            data-testid={`remove-${productId}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          className="bg-cyan-500 hover:bg-cyan-600 text-white h-8"
                          onClick={() => addToCart(product)}
                          data-testid={`add-${productId}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Ajouter
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Excluded Products due to Allergies */}
      {diagnosisResult?.excluded_products?.length > 0 && (
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader className="py-2">
            <CardTitle className="text-sm font-medium text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Produits exclus (allergies)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-red-600 mb-2">
              Ces produits ont été retirés des recommandations en raison des allergies signalées :
            </p>
            <div className="space-y-1">
              {diagnosisResult.excluded_products.map((product, pIndex) => (
                <div key={pIndex} className="flex items-center justify-between text-sm p-2 bg-white/50 rounded">
                  <div className="flex-1">
                    <span className="text-gray-600 line-through">{product.name}</span>
                    {product.excluded_reason && (
                      <Badge variant="destructive" className="text-xs ml-2">
                        {product.excluded_reason}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Manual Product Search */}
      <Card className="border-dashed border-2 border-[#E8B4B8]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-[#B07F83]" />
              Ajouter un produit manuellement
            </span>
            {!showProductSearch && (
              <Button 
                size="sm" 
                variant="outline"
                className="text-xs border-[#E8B4B8] text-[#B07F83]"
                onClick={() => setShowProductSearch(true)}
                data-testid="show-product-search"
              >
                Rechercher
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        
        {showProductSearch && (
          <CardContent className="pt-0">
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher (ex: Château Rouge Bébé...)"
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="product-search-input"
                />
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => {
                    setShowProductSearch(false);
                    setProductSearchQuery('');
                    setProductSearchResults([]);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {searchingProducts && (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm text-gray-500">Recherche...</span>
                </div>
              )}
              
              {!searchingProducts && productSearchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {productSearchResults.map((product, idx) => {
                    const productId = product.id || product.code || product.name;
                    const inCart = isInCart(productId);
                    
                    return (
                      <div 
                        key={idx}
                        className={`p-2 cursor-pointer flex items-center justify-between ${
                          inCart ? 'bg-[#FAE5E7]' : 'hover:bg-[#FDF2F3]'
                        }`}
                        data-testid={`search-result-${idx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{product.name}</p>
                          <p className="text-xs text-gray-500">
                            {product.brand || product.category || product.source}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-[#B07F83]">
                            {product.price?.toLocaleString('fr-FR')} F
                          </span>
                          {inCart ? (
                            <Check className="w-4 h-4 text-[#B07F83]" />
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 hover:bg-[#F5D0D3]"
                              onClick={() => addToCart(product)}
                            >
                              <Plus className="w-4 h-4 text-[#C08B8F]" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {!searchingProducts && productSearchQuery.length >= 2 && productSearchResults.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-2">Aucun produit trouvé</p>
              )}
            </div>
          </CardContent>
        )}
      </Card>
      
      {/* Cart Summary - Unified with adult */}
      <Card className="bg-[#FDF2F3] border-[#F5D0D3]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-pink-700 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            Panier ({cartItemsCount} article{cartItemsCount > 1 ? 's' : ''})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {/* Cart items list */}
          {cart.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {cart.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between bg-white p-2 rounded-lg text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.name}</p>
                    {item.routine_step && (
                      <Badge variant="outline" className="text-xs">
                        {ROUTINE_STEPS[item.routine_step]?.label || item.routine_step}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => updateCartQuantity(item.product_id, -1)}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-4 text-center text-xs">{item.quantity}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => updateCartQuantity(item.product_id, 1)}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <span className="text-pink-700 font-semibold whitespace-nowrap">
                      {(item.price * item.quantity).toLocaleString('fr-FR')} F
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-red-500 hover:bg-red-100"
                      onClick={() => removeFromCart(item.product_id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {cart.length === 0 && (
            <p className="text-sm text-[#B07F83] text-center py-2">Panier vide</p>
          )}
          
          {/* Total */}
          <div className="flex justify-between items-center pt-2 border-t border-[#F5D0D3]">
            <span className="font-medium">Total</span>
            <span className="text-2xl font-bold text-pink-700">
              {cartTotal.toLocaleString('fr-FR')} FCFA
            </span>
          </div>
        </CardContent>
      </Card>
      
      {/* Safety Warning */}
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-semibold mb-1">Important</p>
          <p>En cas de doute ou de problème persistant, consulter un professionnel de santé (pédiatre, dermatologue).</p>
        </div>
      </div>
      
      {/* Practical Tips */}
      {diagnosisResult?.tips && diagnosisResult.tips.length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Conseils pratiques
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-2">
              {diagnosisResult.tips.map((tip, idx) => (
                <li key={idx} className="text-sm text-green-700 flex items-start gap-2">
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      
      {/* Share & Print Actions */}
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          className="flex-1 border-green-500 text-green-600 hover:bg-green-50"
          onClick={shareViaWhatsApp}
          data-testid="share-whatsapp-btn"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          WhatsApp
        </Button>
        <Button 
          variant="outline" 
          className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50"
          onClick={printRecommendations}
          data-testid="print-btn"
        >
          <Printer className="w-4 h-4 mr-2" />
          Imprimer
        </Button>
      </div>
      
      {/* Main Actions */}
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={() => {
            setStep(1);
            setDiagnosisResult(null);
            setManualProducts([]);
          }}
          data-testid="new-baby-diagnosis-btn"
        >
          Nouveau conseil
        </Button>
        <Button 
          className="flex-1 bg-[#B07F83] hover:bg-pink-700"
          onClick={() => {
            if (diagnosisResult && onComplete) {
              onComplete({
                ...diagnosisResult,
                manualProducts
              });
            }
          }}
          disabled={!diagnosisResult}
          data-testid="view-products-btn"
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Voir les produits
        </Button>
      </div>
    </div>
  );

  // Generate WhatsApp message
  const generateWhatsAppMessage = () => {
    if (!diagnosisResult) return '';
    
    let message = `🏥 *PHARMACIE SADA - Conseil Bébé*\n\n`;
    message += `👶 *Bébé:* ${babyName}\n`;
    message += `📅 *Date:* ${new Date().toLocaleDateString('fr-FR')}\n`;
    message += `🎂 *Âge:* ${BABY_AGE_CATEGORIES.find(c => c.id === ageCategory)?.label}\n`;
    if (babyWeight) message += `⚖️ *Poids:* ${babyWeight} kg\n`;
    if (babyAllergies) message += `⚠️ *Allergies:* ${babyAllergies}\n`;
    
    message += `\n*Problématiques:*\n`;
    selectedConcerns.forEach(c => {
      message += `  • ${getConcernLabel(c)}\n`;
    });
    
    message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    
    // Routine
    if (diagnosisResult.routine) {
      message += `\n*🛡️ Routine conseillée:*\n`;
      Object.entries(diagnosisResult.routine).forEach(([step, data]) => {
        const stepInfo = ROUTINE_STEPS[step];
        if (data && stepInfo) {
          message += `\n${stepInfo.icon} *${stepInfo.label}:*\n`;
          message += `${data.advice}\n`;
        }
      });
    }
    
    message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    
    // Products from cart
    if (cart.length > 0) {
      message += `\n*🛒 Panier (${cartItemsCount} articles):*\n`;
      cart.forEach(item => {
        message += `  • ${item.name} x${item.quantity} - ${(item.price * item.quantity).toLocaleString('fr-FR')} FCFA\n`;
      });
    }
    
    message += `\n💵 *TOTAL:* ${cartTotal.toLocaleString('fr-FR')} FCFA\n`;
    
    // Tips
    if (diagnosisResult.tips?.length > 0) {
      message += `\n*💡 Conseils:*\n`;
      diagnosisResult.tips.forEach(tip => {
        message += `  ✓ ${tip}\n`;
      });
    }
    
    message += `\n⚠️ _En cas de doute, consulter un professionnel de santé._\n`;
    message += `\n📍 Pharmacie Saint Ange des Amis\n`;
    
    return message;
  };

  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    const message = generateWhatsAppMessage();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Ouverture de WhatsApp...');
  };

  // Print recommendations
  const printRecommendations = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Conseil Bébé - ${babyName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #EC4899; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { color: #EC4899; margin: 0; font-size: 24px; }
          .baby-info { background: #FDF2F8; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .concerns { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0; }
          .concern-badge { background: #FCE7F3; color: #BE185D; padding: 4px 12px; border-radius: 9999px; font-size: 12px; }
          .routine { margin: 20px 0; }
          .routine-step { padding: 12px; border-radius: 8px; margin-bottom: 10px; }
          .routine-cleanse { background: #DBEAFE; color: #1E40AF; }
          .routine-moisturize { background: #CFFAFE; color: #0E7490; }
          .routine-protect { background: #D1FAE5; color: #047857; }
          .routine-treat { background: #FEF3C7; color: #B45309; }
          .products { margin: 20px 0; }
          .product { display: flex; justify-content: space-between; padding: 10px; border-bottom: 1px solid #f0f0f0; }
          .product-price { color: #EC4899; font-weight: bold; }
          .total { background: #FDF2F8; padding: 15px; border-radius: 8px; text-align: center; margin: 20px 0; }
          .total-amount { font-size: 24px; color: #BE185D; font-weight: bold; }
          .warning { background: #FEF3C7; border: 1px solid #F59E0B; border-radius: 8px; padding: 12px; margin: 20px 0; }
          .tips { background: #ECFDF5; padding: 15px; border-radius: 8px; margin: 20px 0; }
          .footer { text-align: center; margin-top: 30px; padding-top: 15px; border-top: 1px solid #e5e7eb; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>👶 PHARMACIE SADA - Conseil Bébé</h1>
          <p>Recommandations Dermo-Cosmétiques</p>
        </div>
        
        <div class="baby-info">
          <strong>👶 Bébé:</strong> ${babyName}<br>
          <strong>📅 Date:</strong> ${new Date().toLocaleDateString('fr-FR')}<br>
          <strong>🎂 Âge:</strong> ${BABY_AGE_CATEGORIES.find(c => c.id === ageCategory)?.label}
          ${babyWeight ? `<br><strong>⚖️ Poids:</strong> ${babyWeight} kg` : ''}
          ${babyAllergies ? `<br><strong>⚠️ Allergies:</strong> ${babyAllergies}` : ''}
        </div>
        
        <div>
          <strong>Problématiques identifiées:</strong>
          <div class="concerns">
            ${selectedConcerns.map(c => `<span class="concern-badge">${getConcernLabel(c)}</span>`).join('')}
          </div>
        </div>
        
        ${diagnosisResult?.routine ? `
          <div class="routine">
            <h3>🛡️ Routine conseillée</h3>
            ${Object.entries(diagnosisResult.routine).map(([step, data]) => {
              const stepInfo = ROUTINE_STEPS[step];
              if (!data || !stepInfo) return '';
              return `<div class="routine-step routine-${step}">
                <strong>${stepInfo.icon} ${stepInfo.label}</strong><br>
                ${data.advice}
              </div>`;
            }).join('')}
          </div>
        ` : ''}
        
        ${cart.length > 0 ? `
          <div class="products">
            <h3>🛒 Panier (${cartItemsCount} articles)</h3>
            ${cart.map(item => `
              <div class="product">
                <span>${item.name} x${item.quantity}</span>
                <span class="product-price">${(item.price * item.quantity).toLocaleString('fr-FR')} FCFA</span>
              </div>
            `).join('')}
          </div>
        ` : ''}
        
        <div class="total">
          <p>Total</p>
          <p class="total-amount">${cartTotal.toLocaleString('fr-FR')} FCFA</p>
        </div>
        
        ${diagnosisResult?.tips?.length > 0 ? `
          <div class="tips">
            <strong>💡 Conseils pratiques</strong>
            <ul>
              ${diagnosisResult.tips.map(tip => `<li>${tip}</li>`).join('')}
            </ul>
          </div>
        ` : ''}
        
        <div class="warning">
          <strong>⚠️ Important</strong><br>
          En cas de doute ou de problème persistant, consulter un professionnel de santé (pédiatre, dermatologue).
        </div>
        
        <div class="footer">
          <p>📍 Pharmacie Saint Ange des Amis</p>
          <p>📧 contact-info@phciesada.org</p>
          <p>Merci de votre confiance!</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
    
    toast.success('Ouverture de l\'impression...');
  };

  // Navigation
  const handleNext = () => {
    if (step === 1 && canProceedStep1) setStep(2);
    else if (step === 2 && canProceedStep2) setStep(3);
    else if (step === 3 && canProceedStep3) submitDiagnosis();
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const getNextDisabled = () => {
    if (step === 1) return !canProceedStep1;
    if (step === 2) return !canProceedStep2;
    if (step === 3) return !canProceedStep3;
    return false;
  };

  return (
    <div className="max-w-xl mx-auto pb-6">
      <Card className="border-[#F5D0D3]">
        <CardContent className="p-4 sm:p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          
          {/* Navigation buttons */}
          {step < 4 && (
            <div className="flex justify-between mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={step === 1 ? onCancel : handleBack}
                disabled={loading}
                data-testid="baby-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {step === 1 ? 'Annuler' : 'Retour'}
              </Button>
              
              <Button
                onClick={handleNext}
                disabled={getNextDisabled() || loading}
                className="bg-[#B07F83] hover:bg-pink-700"
                data-testid="baby-next-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyse...
                  </>
                ) : step === 3 ? (
                  <>
                    <Heart className="w-4 h-4 mr-2" />
                    Conseiller
                  </>
                ) : (
                  <>
                    Suivant
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BabyDiagnosis;
